import {
    Component,
    OnInit,
    OnDestroy,
    HostBinding,
    HostListener,
    ElementRef,
    Renderer2,
    ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import {
    style,
    animate,
    transition,
    trigger,
    query as q,
    stagger
} from '@angular/animations';
export function query(s, a, o = {optional: true}) {
    return q(s, a, o);
}
import { BehaviorSubject, Subject, from, throwError } from 'rxjs';
import { map, tap, switchMap, catchError, retryWhen } from 'rxjs/operators';

import { AlertService, ConfirmService } from '../../ui.module';

import {
    AuthService,
    LocalStorageService,
} from '../../core/upgraded-providers';

import { SessionService } from '../../session.service';
import { GreenworksService } from '../../greenworks.service';

import { PrivateServersService } from '../private-servers.service';
import { CommunityServersService } from '../community-servers.service';
import { EServerStatus } from '../server-status.enum';

import { NwMenuService } from '../nw-menu.service';

import OFFICIAL_SERVER from './official-server';
import LOCALHOST_SERVER from './localhost-server';

const nw = window.nw;

const IS_COMMUNITY = 'community';
const IS_PRIVATE = 'private';

const OFFICIAL_SERVER_USERS = 'official_server_users';

function sortByLike(a, b) {
    return a.likeCount > b.likeCount ? -1 : 1;
}

let requestAnimationFrameId: number = null;

@Component({
    selector: 'app-nw-menu',
    templateUrl: './nw-menu.component.pug',
    styleUrls: ['./nw-menu.component.less'],
    animations: [
        trigger('list', [
            transition('* => *', [ // each time the binding value changes
                query(':enter', [
                    style({ opacity: 0 }),
                    stagger(100, [
                        animate('.3s', style({ opacity: 1 }))
                    ])
                ])
            ])
        ]),
    ],
    providers: [SessionService, GreenworksService, PrivateServersService, CommunityServersService]
})
export class NwMenuComponent implements OnInit, OnDestroy {
    @HostBinding('class.--ui') _ui = true;
    @HostBinding('class.loaded') loaded = false;

    @ViewChild('serversList', {static: false}) serversList: ElementRef;
    @ViewChild('serversWrap', {static: false}) serversWrap: ElementRef;
    @ViewChild('serversWrapShadow', {static: false}) serversWrapShadow: ElementRef;
    @ViewChild('serversCover', {static: false}) serversCover: ElementRef;

    public IS_COMMUNITY: string = IS_COMMUNITY;
    public IS_PRIVATE: string = IS_PRIVATE;
    public IS_ACTIVE: string = EServerStatus.IS_ACTIVE;
    public IS_LOADING: string = EServerStatus.IS_LOADING;

    public tabServer: string = this.IS_COMMUNITY;

    _localhostServerSbj = new BehaviorSubject(LOCALHOST_SERVER);
    public localhostServer$ = this._localhostServerSbj.asObservable();

    _officialServerSbj = new BehaviorSubject(OFFICIAL_SERVER);
    public officialServer$ = this._officialServerSbj.asObservable(); 

    public communityServers$ = this._communityServersSrv.data$
        .pipe(map((servers: IServer[]) => servers.filter(({ isLiked }) => !isLiked )))
        .pipe(map((servers: IServer[]) => servers.sort(sortByLike)));

    public communityLikedServers$ = this._communityServersSrv.data$
        .pipe(map((servers: IServer[]) => servers.filter(({ isLiked }) => isLiked )))
        .pipe(map((servers: IServer[]) => servers.sort(sortByLike)));

    public privateServers$ = this._privateServersSrv.data$;

    _connectSbj: Subject<IServer> = new Subject();
    _connection: {
        resolve: (value: void | PromiseLike<void>) => void,
        reject: (reason?: any) => void
    } = null;

    constructor(
        private _router: Router,
        private _renderer: Renderer2,

        private _nwMenuSrv: NwMenuService,

        private _sessionSrv: SessionService,
        private _alertService: AlertService,
        private _confirmSrv: ConfirmService,
        private _authSrv: AuthService,
        private _localStorageService: LocalStorageService,

        private _privateServersSrv: PrivateServersService,
        private _communityServersSrv: CommunityServersService
    ) {
        const connecting$ = this._connectSbj.asObservable()
            .pipe(tap((server) => console.debug('NwMenuComponent::connect', server.apiUrl)))
            .pipe(switchMap((server) => from(this._nwMenuSrv.connect(server)).pipe(
                map(() => server),
                tap((server) => this._activateDiscordRPC(server)),
                tap((server) => console.debug('NwMenuComponent::signInSteam', server.apiUrl)),
                switchMap((server) => from(this._authSrv.signInSteamByTicket()).pipe(map(() => server))),
                tap((server) => console.debug('NwMenuComponent::navigateTo', server.apiUrl)),
                tap((server) => {
                    setTimeout(() => {
                        this._router.navigate([server.settings.official ? '/enter' : '/map']);
                    });
                })
            )));

        connecting$
            .pipe(tap(() => {
                this._connection.resolve();
            }))
            .pipe(catchError((err) => {
                this._error(err);
                this._connection.reject(err);

                return throwError(err);
            }))
            .pipe(retryWhen(errors => errors))
            .subscribe();
    }

    ngOnInit() {
        this._sessionSrv.server = null;

        // Deferred start for animation.
        setTimeout(() => this.loaded = true, 0);

        // We need this because localStorage grows too large and exceeds 5 MB capacity
        // TODO: Rework this to get rid of unnecessary localStorage bloat
        this._localStorageService.remove('users.code.activeWorld', 'local');
        this._localStorageService.remove('users.code.activeSim', 'local');
        this._localStorageService.remove('branches', 'local');
        this._localStorageService.remove('users.code.activeWorld');
        this._localStorageService.remove('users.code.activeSim');
        this._localStorageService.remove('branches');

        const loop = () => {
            this.onMouseWheel();

            requestAnimationFrameId = requestAnimationFrame(loop);
        }

        loop();
    }

    ngOnDestroy() {
        cancelAnimationFrame(requestAnimationFrameId);
    }

    onVersionData(server: IServer, versionData: IVersionData) {
        if (!versionData) {
            
        }

        server.users = versionData.users;
        server.packageVersion = versionData.packageVersion;
    }

    async connect(server: IServer): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (this._connection && this._connection.resolve) {
                this._connection.resolve();
                this._connection = null;
            }

            this._connection = { resolve, reject };
            this._connectSbj.next(server);
        });
    }

    async connectOfficialServer(next: () => void) {
        try {
            await this.connect(OFFICIAL_SERVER);
        } catch (err) {
            this._officialServerSbj.next({
                ...this._officialServerSbj.getValue(),
                status: EServerStatus.IS_UNACTIVE
            });
        }

        next();
    }

    async connectLocalhostServer(next: () => void) {
        try {
            await this.connect(LOCALHOST_SERVER);
        } catch (err) {
            this._localhostServerSbj.next({
                ...this._localhostServerSbj.getValue(),
                status: EServerStatus.IS_UNACTIVE
            });
        }

        next();
    }

    async connectCommunityServer(server: IServer, next: () => void) {
        try {
            await this.connect(server);
        } catch (err) {
            this._communityServersSrv.patch(server, {
                status: EServerStatus.IS_UNACTIVE
            });
        }

        next();
    }

    async connectPrivateServer(server: IServer, next: () => void) {
        if (server.isFavorite && !next) {
            this._privateServersSrv.add(server);
        }

        try {
            await this.connect(server);
        } catch (err) {
            this._privateServersSrv.patch(server, {
                status: EServerStatus.IS_UNACTIVE
            });
        }

        next();
    }

    async onRemovePrivate(idx: number) {
        this._privateServersSrv.remove(idx);
    }

    async onLikeServer(server: IServer) {
        try {
            await this._communityServersSrv.likeServer(server);
        } catch (err) {
            this._alert(err);
        }
    }

    async onUnlikeServer(server: IServer) {
        try {
            await this._communityServersSrv.unlikeServer(server);
        } catch (err) {
            this._alert(err);
        }
    }

    updateOfficialServerVersionData(versionData: IVersionData) {
        let data: any = {};
        if (versionData) {
            data.users = versionData.users.toString();
            localStorage.setItem(OFFICIAL_SERVER_USERS, data.users);
            data.status = EServerStatus.IS_ACTIVE;
        } else {
            data.users = localStorage.getItem(OFFICIAL_SERVER_USERS);
            data.status = EServerStatus.IS_UNACTIVE;
        }

        this._officialServerSbj.next({
            ...this._officialServerSbj.getValue(),
            ...data
        });
    }

    updateLocalhostServerVersionData(versionData: IVersionData) {
        let data: any = {};
        if (versionData) {
            data.users = versionData.users.toString();
            data.status = EServerStatus.IS_ACTIVE;
        } else {
            data.status = EServerStatus.IS_UNACTIVE;
        }

        this._localhostServerSbj.next({
            ...this._localhostServerSbj.getValue(),
            ...data
        });
    }

    updateCommunityServerVersionData(server: IServer, versionData: IVersionData) {
        let status = EServerStatus.IS_UNACTIVE;
        let users = null;
        let packageVersion = null;
        if (versionData) {
            status = EServerStatus.IS_ACTIVE;
            ({ users, packageVersion } = versionData);
        }

        this._communityServersSrv.patch(server, {
            status,

            users,
            packageVersion
        });
    }

    updatePrivateServerVersionData(server: IServer, versionData: IVersionData) {
        let status = EServerStatus.IS_UNACTIVE;
        let users = null;
        let packageVersion = null;
        if (versionData) {
            status = EServerStatus.IS_ACTIVE;
            ({ users, packageVersion } = versionData);
        }

        this._privateServersSrv.patch(server, {
            status,

            users,
            packageVersion
        });
    }

    private _error(err) {
        if (err && err.status === -1) {
            this._alert('Connection cannot be established. Please check your network settings and try again.');
        } else if (err === 'protocol') {
            this._alert('Your client version is incompatible with this server version.');
        } else if (err === 'server down') {
            this._confirm({
                message: 'The server is currently under maintenance. Please try again later.',
                buttonCancelLabel: 'Open status page'
            })
                .catch(() => nw.Shell.openExternal('http://status.screeps.com'));
        } else if (err === 'incorrect server password') {
            this._alert('The password for this server is incorrect. Access denied.');
        } else if (err !== 'timeout') {
            this._alert('An error occurred while connecting to the server. Please try again later.');
        }
    }

    private _activateDiscordRPC(server: IServer) {
        const { official, host, port } = server.settings;

        try {
            const clientId = '863212615228063784';

            const DiscordRPC = nw.require('discord-rpc');
            DiscordRPC.register(clientId);
            const rpc = new DiscordRPC.Client({ transport: 'ipc' });
            const startTimestamp = Math.round(Date.now() / 1000);

            rpc.on('ready', () => {
                rpc.setActivity({
                    details: 'Server: ' + (official ? 'screeps.com' : `${ host }:${ port }`),
                    startTimestamp,
                    largeImageKey: 'world',
                    instance: false,
                }).catch(console.error);
            });

            rpc.login({ clientId }).catch(console.error);
        } catch (err) {
            // Noop.
        }
    }

    onMouseWheel() {
        if (!this.serversList) {
            return;
        }

        const listHeight = this.serversList.nativeElement.offsetHeight;
        const paddingTop = window.innerHeight / 100 * 27.8;

        this._renderer.setStyle(this.serversCover.nativeElement, 'minHeight', `${ listHeight + 40 }px`);

        const scrollY = window.scrollY;
        const t = paddingTop - 30 + 38 + 40;
        // 30 = logo top offset
        // 38 = height of logo
        // 40 = nav offset

        if (scrollY > t) {
            const scrollTop = scrollY - t;
            const a = Math.floor(Math.sin((scrollTop > 90 ? 90 : scrollTop) * Math.PI / 180) * 30);
            const shadowTop = scrollTop + a;

            this.serversWrap.nativeElement.scrollTop = scrollTop;

            this._renderer.setStyle(this.serversWrapShadow.nativeElement, 'top', `${ shadowTop }px`);

            this._renderer.setStyle(this.serversWrap.nativeElement, 'position', `fixed`);
            this._renderer.setStyle(this.serversWrap.nativeElement, 'top', `${ 225 }px`);
            this._renderer.setStyle(this.serversWrap.nativeElement, 'bottom', `0px`);
        } else {

            this._renderer.setStyle(this.serversWrapShadow.nativeElement, 'top', null);

            this._renderer.setStyle(this.serversWrap.nativeElement, 'position', null);
            this._renderer.setStyle(this.serversWrap.nativeElement, 'top', null);
            this._renderer.setStyle(this.serversWrap.nativeElement, 'bottom', null);
        }
    }

    trackServerByFn(index, item) {
        return item._id;
    }

    private _alert(message) {
        return this._alertService.show({ data: {
            message
        }});
    }

    private _confirm(data) {
        return this._confirmSrv.show({
            data
        });
    }
}
